// 光驅散黑暗 — fogged lineart topic progress (practice-loop-v2).
//
// The target topic's lineart thumb sits under N fog tiles (N = topic total).
// Each distinct-correct clears its tile; the final one triggers fullClear
// (whole image brightens) before the unlock overlay takes over.
//
// Tile mapping is positional over the sorted known-qid list (fog.ts). The
// known list is correctQids ∪ current queue qids; in the normal flow that
// is the topic's full question set. Tiles are anonymous fog — if the known
// list is ever partial, the trailing tiles simply stay fogged.

import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { useState } from 'react'
import type { PackId, QueueTargetTopic } from '@skena/schema'
import { clearedTileIndexes, itemIdForTopic } from './fog.ts'

export function TopicFogReveal({
  packId,
  targetTopic,
  roundCorrectQids,
  topicQueueQids,
  fullClear,
}: {
  packId: PackId
  targetTopic: QueueTargetTopic
  roundCorrectQids: ReadonlySet<string>
  /** qids of the current queue belonging to the target topic. */
  topicQueueQids: readonly string[]
  /** True once the last distinct-correct landed — plays the whole-image brighten. */
  fullClear: boolean
}) {
  const reducedMotion = useReducedMotion()
  const [imgFailed, setImgFailed] = useState(false)

  const live = new Set<string>(targetTopic.correctQids)
  for (const qid of roundCorrectQids) live.add(qid)
  const known = [...new Set([...targetTopic.correctQids, ...topicQueueQids])]
  const cleared = clearedTileIndexes(known, live)
  const total = Math.max(targetTopic.total, known.length)
  const liveDistinct = Math.min(targetTopic.total, live.size)
  const thumbUrl = `/assets/packs/${packId}/items/${itemIdForTopic(packId, targetTopic.topicId)}-lineart-thumb.png`

  return (
    <motion.div
      className="skena-frame relative overflow-hidden rounded-[var(--radius-soft)] border border-[var(--color-decoration)]/30 bg-white/60 shadow-[var(--shadow-card)]"
      aria-label={`攻略中：${targetTopic.nameZh}，已驅散 ${liveDistinct} / ${targetTopic.total}`}
      animate={fullClear && !reducedMotion ? { scale: [1, 1.02, 1] } : {}}
      transition={{ duration: 0.8, ease: 'easeOut' }}
    >
      <div className="relative flex items-center justify-between px-4 pt-3 text-sm">
        <span className="text-[var(--color-fg)]">
          <span className="text-[var(--color-muted)]">攻略中：</span>
          <span aria-hidden="true" className="text-[var(--color-decoration)]">✦</span>{' '}
          <span className="font-semibold">{targetTopic.nameZh}</span>
        </span>
        <span className="text-xs tabular-nums text-[var(--color-muted)]">
          {liveDistinct}/{targetTopic.total}
        </span>
      </div>
      {imgFailed ? (
        // Naming-convention fallback: keep the numeric progress readable.
        <div className="px-4 pb-3 pt-1 text-xs text-[var(--color-muted)]">
          再答對 {Math.max(0, targetTopic.total - liveDistinct)} 題就解鎖線稿！
        </div>
      ) : (
        <div className="relative mx-4 mb-3 mt-2 overflow-hidden rounded-lg">
          <img
            src={thumbUrl}
            alt=""
            className="block w-full"
            style={{ aspectRatio: '1408 / 786' }}
            onError={() => setImgFailed(true)}
          />
          <div className="absolute inset-0 flex" aria-hidden="true">
            {Array.from({ length: total }, (_, idx) => (
              <AnimatePresence key={idx}>
                {!cleared.has(idx) && !fullClear ? (
                  <motion.div
                    className="h-full flex-1"
                    style={{
                      background:
                        'linear-gradient(180deg, rgba(148,134,110,0.92) 0%, rgba(120,108,88,0.96) 100%)',
                      boxShadow: 'inset 0 0 12px rgba(255,255,255,0.25)',
                    }}
                    initial={false}
                    exit={
                      reducedMotion
                        ? { opacity: 0 }
                        : { opacity: 0, filter: 'blur(6px)', transition: { duration: 0.7, ease: 'easeOut' } }
                    }
                  />
                ) : (
                  // Cleared slot keeps its flex share so neighbors don't stretch.
                  <div className="h-full flex-1" />
                )}
              </AnimatePresence>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  )
}
